import { useEffect, useId, useRef, useState } from "react";
import { Link } from "react-router-dom";

export function TermsAcceptanceModal({ open, currentTerms, blocking, busy, error, onAccept, onClose, onSignOut }) {
  const [checked, setChecked] = useState(false);
  const [helper, setHelper] = useState("");
  const titleId = useId();
  const descriptionId = useId();
  const dialogRef = useRef(null);
  const checkboxRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setChecked(false);
    setHelper("");
    requestAnimationFrame(() => dialogRef.current?.focus());
  }, [open, currentTerms?.id]);

  useEffect(() => {
    if (!open) return undefined;

    function onKeyDown(event) {
      if (event.key === "Escape" && !blocking) onClose();
      if (event.key !== "Tab") return;
      const focusable = dialogRef.current?.querySelectorAll("a[href],button,input,[tabindex]:not([tabindex='-1'])");
      if (!focusable?.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [blocking, onClose, open]);

  if (!open) return null;

  const versionId = currentTerms?.id || "";
  const termsHref = versionId ? `/terms?version=${encodeURIComponent(versionId)}` : "/terms";

  function submit() {
    if (!checked) {
      setHelper("Check the box to accept the current Terms of Service.");
      checkboxRef.current?.focus();
      return;
    }
    onAccept();
  }

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/70 px-4 py-6">
      <section
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        tabIndex={-1}
        className="w-full max-w-md rounded-lg border border-zinc-700 bg-zinc-950 p-5 shadow-2xl outline-none"
      >
        <div className="space-y-3">
          <div className="flex items-start justify-between gap-3">
            <div className="space-y-1">
              <h2 id={titleId} className="text-lg font-semibold text-zinc-50">
                Please review and accept our updated terms
              </h2>
              <p className="text-xs text-zinc-400">
                {currentTerms?.versionLabel || "Current version"} · {currentTerms?.updatedOn || ""}
              </p>
            </div>
            {!blocking ? (
              <button type="button" className="theme-button-secondary px-3 py-1 text-xs" onClick={onClose}>
                Close
              </button>
            ) : null}
          </div>
          <p id={descriptionId} className="text-sm leading-6 text-zinc-300">
            You can keep browsing, but creating markets and casting votes require accepting the current Terms of Service.
          </p>
          <Link to={termsHref} className="theme-link text-sm" onClick={onClose}>
            Read the full terms
          </Link>
          <label className="flex items-start gap-3 rounded-md border border-zinc-800 bg-zinc-900/70 p-3 text-sm text-zinc-200">
            <input
              ref={checkboxRef}
              type="checkbox"
              checked={checked}
              onChange={(event) => {
                setChecked(event.target.checked);
                setHelper("");
              }}
              className="mt-1"
            />
            <span>I have reviewed and agree to the current Terms of Service.</span>
          </label>
          {helper ? <p className="text-sm text-amber-200">{helper}</p> : null}
          {error ? <p className="text-sm text-rose-300">{error}</p> : null}
          <div className="grid gap-2 sm:grid-cols-2">
            <button type="button" className="theme-button" disabled={busy} onClick={submit}>
              {busy ? "Accepting..." : "Accept terms"}
            </button>
            <button type="button" className="theme-button-secondary" disabled={busy} onClick={onClose}>
              Not now
            </button>
          </div>
          <button type="button" className="theme-link text-sm" disabled={busy} onClick={onSignOut}>
            Sign out instead
          </button>
        </div>
      </section>
    </div>
  );
}
